// « Demandes sans suite » (PB9, PMS-PHONE-BOOKING-BRIEF §2.1) — `pms_turnaway`.
//
// Browser-scoped: the desk's client. The write is one RPC (pms.reservation.write
// and the établissement in reach, L4); the read is an invoker function, so the
// table's RLS decides what is seen. Nothing sent or read names a person.

import type { AgeBand, IsoDate, Result, Turnaway, TurnawayInput, TurnawayRepository } from '@lemontaigu/pms-core';
import { fromList, fromResult, type SupabaseClient } from './support.js';

interface TurnawayRow {
  id: number;
  property_code: string;
  recorded_at: string;
  channel: 'phone' | 'web';
  arrival: string;
  nights: number;
  persons: string[];
  quoted_room_type_id: number | null;
  quoted_rate_plan_id: number | null;
  quoted_total_cents: number | null;
  kind: 'denial' | 'regret';
}

export const rowToTurnaway = (r: TurnawayRow): Turnaway => ({
  id: r.id,
  propertyCode: r.property_code,
  recordedAt: r.recorded_at,
  channel: r.channel,
  arrival: r.arrival,
  nights: r.nights,
  persons: r.persons as AgeBand[],
  // The table's CHECK keeps the three together.
  quoted:
    r.quoted_room_type_id === null || r.quoted_rate_plan_id === null || r.quoted_total_cents === null
      ? null
      : { roomTypeId: r.quoted_room_type_id, ratePlanId: r.quoted_rate_plan_id, totalCents: r.quoted_total_cents },
  kind: r.kind,
});

export class SupabaseTurnawayRepository implements TurnawayRepository {
  constructor(private readonly db: SupabaseClient) {}

  async record(input: TurnawayInput): Promise<Result<number>> {
    return fromResult(
      await this.db.rpc('pms_record_turnaway', {
        p: {
          propertyCode: input.propertyCode,
          channel: input.channel ?? 'phone',
          arrival: input.arrival,
          nights: input.nights,
          persons: input.persons,
          quoted: input.quoted,
        },
      }),
    );
  }

  async month(propertyCode: string, month: IsoDate): Promise<Result<Turnaway[]>> {
    return fromList(
      await this.db.rpc('pms_turnaways_month', { p_property_code: propertyCode, p_month: month }),
      (r: TurnawayRow) => rowToTurnaway(r),
    );
  }
}
